import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  UploadResult 
} from 'firebase/storage';
import { storage } from '../firebase/config';

export const storageService = {
  // Upload an image file and return the download URL
  async uploadAvatarItemImage(
    file: File, 
    teamId: string, 
    itemName: string
  ): Promise<string> {
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const sanitizedName = itemName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const fileName = `avatar_items/${teamId}/${sanitizedName}_${timestamp}.${file.name.split('.').pop()}`;
      
      // Create storage reference
      const storageRef = ref(storage, fileName);
      
      // Upload the file
      const uploadResult: UploadResult = await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image. Please try again.');
    }
  },

  // Delete an image from storage
  async deleteAvatarItemImage(imageUrl: string): Promise<void> {
    try {
      // Extract the file path from the URL
      const url = new URL(imageUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
      
      if (!pathMatch) {
        throw new Error('Invalid image URL');
      }
      
      const filePath = decodeURIComponent(pathMatch[1]);
      const storageRef = ref(storage, filePath);
      
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw new Error('Failed to delete image.');
    }
  },

  // Validate image file
  validateImageFile(file: File): { valid: boolean; error?: string } {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Please upload a valid image file (JPEG, PNG, GIF, or WebP)' 
      };
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { 
        valid: false, 
        error: 'Image file size must be less than 5MB' 
      };
    }

    return { valid: true };
  }
};
